import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf/dist/jspdf.umd.min';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/pie';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner
} from 'reactstrap';
import { toast } from 'react-toastify';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import apiBase from '../../apiConfig';
import { exportRowsToXlsx } from '../../helpers/spreadsheet';
import { formatSelectedPeriod } from '../../helpers/testPeriod';
import { emitResultTopicsUpdate } from '../../helpers/resultTopics';
import { getAuthHeaders } from '../../helpers/utils';

const BASE_ENDPOINT = `${apiBase}/p-admin/test-results.php`;
const TOPICS_ENDPOINT = `${BASE_ENDPOINT}?action=topics`;
const TOPIC_ENDPOINT = `${BASE_ENDPOINT}?action=topic`;

const EMPTY_TOPICS = {
  stats: {
    topic_count: 0,
    question_count: 0,
    completed_count: 0,
    student_count: 0
  },
  topics: []
};

const EMPTY_TOPIC = {
  topic: null,
  summary: {
    question_count: 0,
    response_count: 0,
    faculty_count: 0,
    group_count: 0,
    average_ball: 0,
    average_score_percent: 0,
    total_votes: 0
  },
  tables: {
    institute: [],
    faculties: [],
    groups: [],
    courses: [],
    nationalities: [],
    students: []
  },
  questions: []
};

const BAR_COLORS = ['#2c7be5', '#00d27a', '#f5803e', '#e63757'];

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const readQueryParams = search => {
  const params = new URLSearchParams(search || '');
  const result = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
};

const buildSearchWithTopic = (search, topicKey) => {
  const params = new URLSearchParams(search || '');

  if (topicKey) {
    params.set('kalit', topicKey);
  } else {
    params.delete('kalit');
  }

  const next = params.toString();
  return next ? `?${next}` : '';
};

const toFileSafeName = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'natijalar';

const formatPercent = value => `${Number(value || 0).toFixed(1)}%`;
const formatAverageBall = (value, totalQuestions) =>
  totalQuestions > 0 ? `${Number(value || 0).toFixed(1)} / ${totalQuestions}` : Number(value || 0).toFixed(1);

const buildChartOption = question => ({
  color: BAR_COLORS,
  animationDuration: 350,
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    },
    formatter: params => {
      if (!params.length) {
        return '';
      }

      const item = params[0];
      return `${item.axisValue}<br/>${item.value} ta ovoz`;
    }
  },
  grid: {
    top: 28,
    left: 12,
    right: 12,
    bottom: 20,
    containLabel: true
  },
  xAxis: {
    type: 'category',
    data: (question.options || []).map(option => option.label),
    axisTick: {
      alignWithLabel: true
    },
    axisLine: {
      lineStyle: {
        color: '#d8e2ef'
      }
    },
    axisLabel: {
      color: '#5e6e82'
    }
  },
  yAxis: {
    type: 'value',
    minInterval: 1,
    splitLine: {
      lineStyle: {
        color: '#edf2f9'
      }
    },
    axisLabel: {
      color: '#748194'
    }
  },
  series: [
    {
      name: 'Ovozlar',
      type: 'bar',
      barMaxWidth: 42,
      data: (question.options || []).map(option => option.count),
      itemStyle: {
        borderRadius: [10, 10, 0, 0]
      }
    }
  ]
});

const buildSegmentBarOption = rows => ({
  animationDuration: 900,
  animationEasing: 'cubicOut',
  color: ['#2c7be5', '#00d27a'],
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    }
  },
  legend: {
    top: 0,
    right: 0
  },
  grid: {
    top: 38,
    left: 12,
    right: 42,
    bottom: 16,
    containLabel: true
  },
  xAxis: {
    type: 'category',
    data: rows.map(row => row.name),
    axisTick: {
      alignWithLabel: true
    },
    axisLine: {
      lineStyle: {
        color: '#d8e2ef'
      }
    },
    axisLabel: {
      color: '#5e6e82',
      interval: 0
    }
  },
  yAxis: [
    {
      type: 'value',
      minInterval: 1,
      splitLine: {
        lineStyle: {
          color: '#edf2f9'
        }
      },
      axisLabel: {
        color: '#748194'
      }
    },
    {
      type: 'value',
      min: 0,
      max: 100,
      splitLine: { show: false },
      axisLabel: {
        color: '#748194',
        formatter: '{value}%'
      }
    }
  ],
  series: [
    {
      name: 'Natijalar',
      type: 'bar',
      barMaxWidth: 34,
      data: rows.map(row => row.response_count || 0),
      itemStyle: {
        borderRadius: [10, 10, 0, 0]
      }
    },
    {
      name: "O'rtacha %",
      type: 'bar',
      yAxisIndex: 1,
      barMaxWidth: 22,
      data: rows.map(row => row.average_score_percent || 0),
      itemStyle: {
        borderRadius: [10, 10, 0, 0]
      }
    }
  ]
});

const buildSegmentPieOption = rows => ({
  animationDuration: 1000,
  animationEasing: 'quarticOut',
  color: ['#2c7be5', '#00d27a', '#f5803e', '#e63757', '#27bcfd', '#6b5eae', '#00a693', '#748194'],
  tooltip: {
    trigger: 'item',
    formatter: params => `${params.name}<br/>${params.value} ta natija`
  },
  legend: {
    bottom: 0,
    type: 'scroll',
    icon: 'circle',
    itemWidth: 9,
    itemHeight: 9,
    textStyle: {
      color: '#5e6e82'
    }
  },
  series: [
    {
      name: 'Millati',
      type: 'pie',
      radius: ['43%', '72%'],
      center: ['50%', '42%'],
      itemStyle: {
        borderColor: '#ffffff',
        borderWidth: 3,
        borderRadius: 7
      },
      label: {
        formatter: '{b}\n{c}',
        color: '#1f2937',
        fontWeight: 700
      },
      data: rows.map(row => ({
        name: row.name,
        value: row.response_count || 0
      }))
    }
  ]
});

const exportCanvasToPdf = async (node, fileName) => {
  const canvas = await html2canvas(node, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: node.scrollWidth,
    windowHeight: node.scrollHeight
  });

  const pdf = new jsPDF('p', 'mm', 'a4');
  const imgData = canvas.toDataURL('image/png', 1.0);
  const pageWidth = 210;
  const pageHeight = 297;
  const imgHeight = (canvas.height * pageWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage('a4', 'p');
    pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
    heightLeft -= pageHeight;
  }

  pdf.save(fileName);
};

const CustomTestResults = ({ history, location, match }) => {
  const [topicsData, setTopicsData] = useState(EMPTY_TOPICS);
  const [topicData, setTopicData] = useState(EMPTY_TOPIC);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [loadingTopic, setLoadingTopic] = useState(false);
  const [banner, setBanner] = useState(null);
  const [activeModalKey, setActiveModalKey] = useState('');
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [activePeriodLabel, setActivePeriodLabel] = useState('');
  const exportRef = useRef(null);

  const authHeaders = useMemo(() => getAuthHeaders(), []);
  const requestConfig = useMemo(
    () => ({
      headers: authHeaders
    }),
    [authHeaders]
  );
  const queryParams = useMemo(() => readQueryParams(location.search), [location.search]);
  const selectedTopicKey = decodeURIComponent(queryParams.kalit || match?.params?.topicKey || '').trim();

  const loadTopics = useCallback(async () => {
    setLoadingTopics(true);

    try {
      const response = await axios.get(TOPICS_ENDPOINT, {
        ...requestConfig,
        params: queryParams
      });

      if (response.data?.success) {
        const nextData = response.data.data || EMPTY_TOPICS;
        setTopicsData(nextData);
        setActivePeriodLabel(formatSelectedPeriod(response.data.period));
        emitResultTopicsUpdate(nextData.topics || []);
        return nextData;
      }

      throw new Error(response.data?.message || 'Natijalar mavzulari yuklanmadi.');
    } catch (error) {
      setBanner({
        color: 'danger',
        text: extractErrorMessage(error, 'Natijalar mavzularini yuklashda xatolik yuz berdi.')
      });
      return null;
    } finally {
      setLoadingTopics(false);
    }
  }, [queryParams, requestConfig]);

  const loadTopic = useCallback(
    async topicKey => {
      if (!topicKey) {
        setTopicData(EMPTY_TOPIC);
        return null;
      }

      setLoadingTopic(true);

      try {
        const response = await axios.get(TOPIC_ENDPOINT, {
          ...requestConfig,
          params: {
            ...queryParams,
            kalit: topicKey
          }
        });

        if (response.data?.success) {
          const nextData = response.data.data || EMPTY_TOPIC;
          setTopicData(nextData);
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return nextData;
        }

        throw new Error(response.data?.message || 'Natija tafsiloti yuklanmadi.');
      } catch (error) {
        setTopicData(EMPTY_TOPIC);
        setBanner({
          color: 'warning',
          text: extractErrorMessage(error, 'Natija tafsilotini yuklashda xatolik yuz berdi.')
        });
        return null;
      } finally {
        setLoadingTopic(false);
      }
    },
    [queryParams, requestConfig]
  );

  useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  useEffect(() => {
    if (!loadingTopics && !selectedTopicKey && topicsData.topics.length > 0) {
      history.replace({
        pathname: '/natijalar',
        search: buildSearchWithTopic(location.search, topicsData.topics[0].key)
      });
    }
  }, [history, loadingTopics, location.search, selectedTopicKey, topicsData.topics]);

  useEffect(() => {
    setActiveModalKey('');
    setBanner(null);

    if (!selectedTopicKey) {
      setTopicData(EMPTY_TOPIC);
      return;
    }

    loadTopic(selectedTopicKey);
  }, [loadTopic, selectedTopicKey]);

  const selectedTopic = useMemo(
    () => topicsData.topics.find(topic => topic.key === selectedTopicKey) || topicData.topic || null,
    [selectedTopicKey, topicData.topic, topicsData.topics]
  );

  const modalConfigs = useMemo(() => {
    const totalQuestions = Number(topicData.topic?.question_count || topicData.summary?.question_count || 0);

    const mapRowsWithOrder = rows => (Array.isArray(rows) ? rows : []).map((row, index) => ({ ...row, order: index + 1 }));

    return {
      institute: {
        key: 'institute',
        title: 'Institut kesimi',
        icon: 'globe-americas',
        rows: mapRowsWithOrder(topicData.tables?.institute),
        columns: [
          { dataField: 'order', text: 'T/R', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'name', text: 'Kesim', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'student_count', text: 'Talabalar', classes: 'align-middle' },
          { dataField: 'response_count', text: 'Natijalar', classes: 'align-middle' },
          { dataField: 'faculty_count', text: 'Fakultet', classes: 'align-middle' },
          { dataField: 'group_count', text: 'Guruh', classes: 'align-middle' },
          {
            dataField: 'average_score_percent',
            text: "O'rtacha",
            classes: 'align-middle',
            formatter: value => <Badge className="psi-results-pill">{formatPercent(value)}</Badge>
          }
        ],
        exportColumns: [
          { label: 'Kesim', value: row => row.name },
          { label: 'Talabalar', value: row => row.student_count },
          { label: 'Natijalar', value: row => row.response_count },
          { label: 'Fakultetlar', value: row => row.faculty_count },
          { label: 'Guruhlar', value: row => row.group_count },
          { label: "O'rtacha", value: row => formatPercent(row.average_score_percent) }
        ]
      },
      faculties: {
        key: 'faculties',
        title: 'Fakultet kesimi',
        icon: 'th',
        rows: mapRowsWithOrder(topicData.tables?.faculties),
        columns: [
          { dataField: 'order', text: 'T/R', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'name', text: 'Fakultet', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'student_count', text: 'Talabalar', classes: 'align-middle' },
          { dataField: 'response_count', text: 'Natijalar', classes: 'align-middle' },
          { dataField: 'group_count', text: 'Guruhlar', classes: 'align-middle' },
          {
            dataField: 'average_ball',
            text: 'Ball',
            classes: 'align-middle',
            formatter: value => formatAverageBall(value, totalQuestions)
          },
          {
            dataField: 'average_score_percent',
            text: "O'rtacha",
            classes: 'align-middle',
            formatter: value => <Badge className="psi-results-pill">{formatPercent(value)}</Badge>
          }
        ],
        exportColumns: [
          { label: 'Fakultet', value: row => row.name },
          { label: 'Talabalar', value: row => row.student_count },
          { label: 'Natijalar', value: row => row.response_count },
          { label: 'Guruhlar', value: row => row.group_count },
          { label: 'Ortacha ball', value: row => formatAverageBall(row.average_ball, totalQuestions) },
          { label: "Ortacha foiz", value: row => formatPercent(row.average_score_percent) }
        ]
      },
      groups: {
        key: 'groups',
        title: 'Guruh kesimi',
        icon: 'boxes',
        rows: mapRowsWithOrder(topicData.tables?.groups),
        columns: [
          { dataField: 'order', text: 'T/R', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'name', text: 'Guruh', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'faculty', text: 'Fakultet', classes: 'align-middle' },
          { dataField: 'student_count', text: 'Talabalar', classes: 'align-middle' },
          { dataField: 'response_count', text: 'Natijalar', classes: 'align-middle' },
          {
            dataField: 'average_ball',
            text: 'Ball',
            classes: 'align-middle',
            formatter: value => formatAverageBall(value, totalQuestions)
          },
          {
            dataField: 'average_score_percent',
            text: "O'rtacha",
            classes: 'align-middle',
            formatter: value => <Badge className="psi-results-pill">{formatPercent(value)}</Badge>
          }
        ],
        exportColumns: [
          { label: 'Guruh', value: row => row.name },
          { label: 'Fakultet', value: row => row.faculty },
          { label: 'Talabalar', value: row => row.student_count },
          { label: 'Natijalar', value: row => row.response_count },
          { label: 'Ortacha ball', value: row => formatAverageBall(row.average_ball, totalQuestions) },
          { label: "Ortacha foiz", value: row => formatPercent(row.average_score_percent) }
        ]
      },
      courses: {
        key: 'courses',
        title: 'Kurs kesimi',
        icon: 'stream',
        rows: mapRowsWithOrder(topicData.tables?.courses),
        columns: [
          { dataField: 'order', text: 'T/R', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'name', text: 'Kurs', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'student_count', text: 'Talabalar', classes: 'align-middle' },
          { dataField: 'response_count', text: 'Natijalar', classes: 'align-middle' },
          {
            dataField: 'average_ball',
            text: 'Ball',
            classes: 'align-middle',
            formatter: value => formatAverageBall(value, totalQuestions)
          },
          {
            dataField: 'average_score_percent',
            text: "O'rtacha",
            classes: 'align-middle',
            formatter: value => <Badge className="psi-results-pill">{formatPercent(value)}</Badge>
          }
        ],
        exportColumns: [
          { label: 'Kurs', value: row => row.name },
          { label: 'Talabalar', value: row => row.student_count },
          { label: 'Natijalar', value: row => row.response_count },
          { label: 'Ortacha ball', value: row => formatAverageBall(row.average_ball, totalQuestions) },
          { label: "Ortacha foiz", value: row => formatPercent(row.average_score_percent) }
        ]
      },
      nationalities: {
        key: 'nationalities',
        title: 'Millati kesimi',
        icon: 'id-card',
        rows: mapRowsWithOrder(topicData.tables?.nationalities),
        columns: [
          { dataField: 'order', text: 'T/R', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'name', text: 'Millati', classes: 'align-middle font-weight-semi-bold' },
          { dataField: 'student_count', text: 'Talabalar', classes: 'align-middle' },
          { dataField: 'response_count', text: 'Natijalar', classes: 'align-middle' },
          {
            dataField: 'average_ball',
            text: 'Ball',
            classes: 'align-middle',
            formatter: value => formatAverageBall(value, totalQuestions)
          },
          {
            dataField: 'average_score_percent',
            text: "O'rtacha",
            classes: 'align-middle',
            formatter: value => <Badge className="psi-results-pill">{formatPercent(value)}</Badge>
          }
        ],
        exportColumns: [
          { label: 'Millati', value: row => row.name },
          { label: 'Talabalar', value: row => row.student_count },
          { label: 'Natijalar', value: row => row.response_count },
          { label: 'Ortacha ball', value: row => formatAverageBall(row.average_ball, totalQuestions) },
          { label: "Ortacha foiz", value: row => formatPercent(row.average_score_percent) }
        ]
      },
      students: {
        key: 'students',
        title: 'Talaba kesimi',
        icon: 'users',
        rows: mapRowsWithOrder(topicData.tables?.students),
        columns: [
          { dataField: 'order', text: 'T/R', classes: 'align-middle font-weight-semi-bold' },
          {
            dataField: 'full_name',
            text: 'Talaba',
            classes: 'align-middle',
            formatter: (value, row) => (
              <div className="psi-results-student">
                <strong>{value}</strong>
                <span>
                  ID: {row.talaba_id}
                  {row.passport ? ` • ${row.passport}` : ''}
                </span>
              </div>
            )
          },
          { dataField: 'faculty', text: 'Fakultet', classes: 'align-middle' },
          { dataField: 'group', text: 'Guruh', classes: 'align-middle' },
          { dataField: 'course', text: 'Kurs', classes: 'align-middle' },
          { dataField: 'nationality', text: 'Millati', classes: 'align-middle' },
          {
            dataField: 'ball',
            text: 'Ball',
            classes: 'align-middle',
            formatter: (value, row) => `${value} / ${row.question_count}`
          },
          {
            dataField: 'score_percent',
            text: 'Foiz',
            classes: 'align-middle',
            formatter: value => <Badge className="psi-results-pill">{formatPercent(value)}</Badge>
          },
          { dataField: 'finished_at', text: 'Yakunlangan vaqt', classes: 'align-middle' }
        ],
        exportColumns: [
          { label: 'Talaba ID', value: row => row.talaba_id },
          { label: 'F.I.O', value: row => row.full_name },
          { label: 'Pasport', value: row => row.passport || '' },
          { label: 'Fakultet', value: row => row.faculty },
          { label: 'Guruh', value: row => row.group },
          { label: 'Kurs', value: row => row.course || '' },
          { label: 'Millati', value: row => row.nationality || '' },
          { label: 'Ball', value: row => `${row.ball} / ${row.question_count}` },
          { label: 'Foiz', value: row => formatPercent(row.score_percent) },
          { label: 'Yakunlangan vaqt', value: row => row.finished_at }
        ]
      }
    };
  }, [topicData]);

  const activeModal = activeModalKey ? modalConfigs[activeModalKey] : null;

  const summaryCards = useMemo(
    () => [
      {
        key: 'responses',
        title: 'Ishtirokchilar',
        value: topicData.summary?.response_count || 0,
        note: 'yakunlangan natijalar',
        icon: 'users',
        tone: 'primary'
      },
      {
        key: 'questions',
        title: 'Savollar',
        value: topicData.summary?.question_count || 0,
        note: 'mavzudagi savollar',
        icon: 'clipboard-list',
        tone: 'info'
      },
      {
        key: 'groups',
        title: 'Guruhlar',
        value: topicData.summary?.group_count || 0,
        note: 'natija bergan guruhlar',
        icon: 'boxes',
        tone: 'warning'
      },
      {
        key: 'average',
        title: "O'rtacha",
        value: formatAverageBall(topicData.summary?.average_ball, topicData.summary?.question_count || 0),
        note: `${formatPercent(topicData.summary?.average_score_percent || 0)} aniqlik`,
        icon: 'bullseye',
        tone: 'success'
      }
    ],
    [topicData.summary]
  );

  const actionCards = useMemo(
    () => [
      {
        key: 'institute',
        title: 'Institut',
        icon: 'globe-americas',
        note: "Bitta umumiy kesim",
        count: modalConfigs.institute.rows.length
      },
      {
        key: 'faculties',
        title: 'Fakultet',
        icon: 'th',
        note: 'Fakultetlar jadvali',
        count: modalConfigs.faculties.rows.length
      },
      {
        key: 'groups',
        title: 'Guruh',
        icon: 'boxes',
        note: 'Guruhlar kesimi',
        count: modalConfigs.groups.rows.length
      },
      {
        key: 'courses',
        title: 'Kurs',
        icon: 'stream',
        note: 'Kurslar kesimi',
        count: modalConfigs.courses.rows.length
      },
      {
        key: 'nationalities',
        title: 'Millati',
        icon: 'id-card',
        note: 'Millatlar kesimi',
        count: modalConfigs.nationalities.rows.length
      },
      {
        key: 'students',
        title: 'Talaba',
        icon: 'users',
        note: 'Talabalar ro`yxati',
        count: modalConfigs.students.rows.length
      }
    ],
    [modalConfigs]
  );

  const courseSegmentRows = useMemo(() => (topicData.tables?.courses || []).slice(0, 8), [topicData.tables]);
  const nationalitySegmentRows = useMemo(() => (topicData.tables?.nationalities || []).slice(0, 8), [topicData.tables]);
  const courseSegmentOption = useMemo(() => buildSegmentBarOption(courseSegmentRows), [courseSegmentRows]);
  const nationalitySegmentOption = useMemo(() => buildSegmentPieOption(nationalitySegmentRows), [nationalitySegmentRows]);

  const exportRows = useMemo(() => {
    if (!activeModal) {
      return [];
    }

    return activeModal.rows.map(row =>
      activeModal.exportColumns.reduce((accumulator, column) => {
        accumulator[column.label] = column.value(row);
        return accumulator;
      }, {})
    );
  }, [activeModal]);

  const handleTopicChange = topicKey => {
    history.push({
      pathname: '/natijalar',
      search: buildSearchWithTopic(location.search, topicKey)
    });
  };

  const handleExcelExport = async () => {
    if (!activeModal || exportRows.length === 0) {
      toast.info('Eksport qilish uchun jadval satrlari mavjud emas.');
      return;
    }

    try {
      await exportRowsToXlsx({
        rows: exportRows,
        sheetName: activeModal.title,
        fileName: `${toFileSafeName(selectedTopic?.title || selectedTopic?.key)}-${toFileSafeName(activeModal.title)}.xlsx`
      });
    } catch (error) {
      toast.error(error.message || 'Excel faylni eksport qilishda xatolik yuz berdi.');
    }
  };

  const handlePdfExport = async () => {
    if (!activeModal || !exportRef.current || exportRows.length === 0) {
      toast.info('PDF eksporti uchun jadval satrlari mavjud emas.');
      return;
    }

    try {
      await exportCanvasToPdf(
        exportRef.current,
        `${toFileSafeName(selectedTopic?.title || selectedTopic?.key)}-${toFileSafeName(activeModal.title)}.pdf`
      );
    } catch (error) {
      toast.error(error.message || 'PDF yuklab olishda xatolik yuz berdi.');
    }
  };

  const hasTopics = topicsData.topics.length > 0;
  const hasResponses = Number(topicData.summary?.response_count || 0) > 0;

  return (
    <>
      <div className="psi-results-heading mb-3">
        <div>
          <div className="psi-results-heading__nav">
            Natijalar{selectedTopic ? ` / ${selectedTopic.title || selectedTopic.key}` : ''}
          </div>
          <h3 className="mb-1">Custom test natijalari</h3>
          <p className="mb-0">Yangi qo`shilgan test mavzulari bo`yicha ovozlar, kesimlar va diagrammalar.</p>
        </div>
        {activePeriodLabel ? (
          <Badge pill className="psi-active-period-badge">
            {activePeriodLabel}
          </Badge>
        ) : null}
      </div>

      {banner ? (
        <Alert color={banner.color} className="mb-3">
          {banner.text}
        </Alert>
      ) : null}

      <Card className="border-0 shadow-sm med-dashboard-card mb-3">
        <CardBody>
          <div className="psi-results-topics">
            {loadingTopics ? (
              <div className="psi-results-empty py-4">
                <Spinner size="sm" color="primary" className="mr-2" />
                Mavzular yuklanmoqda...
              </div>
            ) : null}

            {!loadingTopics && !hasTopics ? (
              <div className="psi-results-empty py-4">Hozircha custom test mavzulari topilmadi.</div>
            ) : null}

            {!loadingTopics &&
              topicsData.topics.map(topic => (
                <button
                  key={topic.key}
                  type="button"
                  className={`psi-results-topic-chip${selectedTopicKey === topic.key ? ' is-active' : ''}`}
                  onClick={() => handleTopicChange(topic.key)}
                >
                  <strong>{topic.title}</strong>
                  <span>
                    {topic.question_count} savol • {topic.completed_count || 0} natija
                  </span>
                </button>
              ))}
          </div>
        </CardBody>
      </Card>

      {selectedTopic ? (
        <Card className="border-0 shadow-sm med-dashboard-card mb-3">
          <CardBody className="psi-results-overview">
            <div className="psi-results-overview__copy">
              <Badge className="psi-results-overview__badge">
                {selectedTopic.question_count || 0} savol
              </Badge>
              <h4 className="mb-1">{selectedTopic.title || selectedTopic.key}</h4>
              <p className="mb-0">
                {topicData.topic?.last_finished_at
                  ? `Oxirgi natija: ${topicData.topic.last_finished_at}`
                  : 'Hali yakunlangan natija mavjud emas.'}
              </p>
            </div>
            <div className="psi-results-overview__meta">
              <span>{topicData.summary?.total_votes || 0} ta umumiy ovoz</span>
              <span>{topicData.summary?.faculty_count || 0} ta fakultet</span>
              <span>{topicData.summary?.course_count || 0} ta kurs</span>
              <span>{topicData.summary?.nationality_count || 0} ta millat</span>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {loadingTopic ? (
        <Card className="border-0 shadow-sm med-dashboard-card mb-3">
          <CardBody className="text-center py-5">
            <Spinner color="primary" />
          </CardBody>
        </Card>
      ) : null}

      {!loadingTopic && selectedTopic ? (
        <>
          <div className="psi-results-summary mb-3">
            {summaryCards.map(item => (
              <Card key={item.key} className={`border-0 shadow-sm med-dashboard-card psi-results-stat psi-results-stat--${item.tone}`}>
                <CardBody>
                  <div className="psi-results-stat__icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <div className="psi-results-stat__copy">
                    <span>{item.title}</span>
                    <strong>{item.value}</strong>
                    <small>{item.note}</small>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>

          <div className="psi-results-actions mb-3">
            {actionCards.map(item => (
              <button
                key={item.key}
                type="button"
                className="psi-results-action-card"
                onClick={() => setActiveModalKey(item.key)}
                disabled={!selectedTopic}
              >
                <span className="psi-results-action-card__icon">
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <strong>{item.title} kesimi</strong>
                <small>{item.note}</small>
                <Badge pill className="psi-results-action-card__badge">
                  {item.count}
                </Badge>
              </button>
            ))}
          </div>

          {hasResponses ? (
            <div className="psi-results-segment-charts mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card psi-results-segment-card">
                <CardBody>
                  <div className="psi-results-segment-card__head">
                    <h5 className="mb-1">Kurs kesimida natijalar</h5>
                    <Badge pill className="psi-results-segment-card__badge">
                      {courseSegmentRows.reduce((sum, row) => sum + Number(row.response_count || 0), 0)}
                    </Badge>
                  </div>
                  {courseSegmentRows.length ? (
                    <ReactEchartsCore
                      echarts={echarts}
                      option={courseSegmentOption}
                      style={{ height: 280 }}
                      notMerge
                      lazyUpdate
                    />
                  ) : (
                    <div className="psi-results-empty">Kurs kesimida natija topilmadi.</div>
                  )}
                </CardBody>
              </Card>
              <Card className="border-0 shadow-sm med-dashboard-card psi-results-segment-card">
                <CardBody>
                  <div className="psi-results-segment-card__head">
                    <h5 className="mb-1">Millati kesimida natijalar</h5>
                    <Badge pill className="psi-results-segment-card__badge">
                      {nationalitySegmentRows.reduce((sum, row) => sum + Number(row.response_count || 0), 0)}
                    </Badge>
                  </div>
                  {nationalitySegmentRows.length ? (
                    <ReactEchartsCore
                      echarts={echarts}
                      option={nationalitySegmentOption}
                      style={{ height: 280 }}
                      notMerge
                      lazyUpdate
                    />
                  ) : (
                    <div className="psi-results-empty">Millati kesimida natija topilmadi.</div>
                  )}
                </CardBody>
              </Card>
            </div>
          ) : null}

          {!hasResponses ? (
            <Card className="border-0 shadow-sm med-dashboard-card">
              <CardBody className="psi-results-empty">
                Ushbu mavzu bo`yicha hali yakunlangan natija mavjud emas.
              </CardBody>
            </Card>
          ) : null}
        </>
      ) : null}

      <Modal
        isOpen={Boolean(activeModal)}
        toggle={() => setActiveModalKey('')}
        size="xl"
        className="psi-results-modal"
      >
        <ModalHeader toggle={() => setActiveModalKey('')}>
          {activeModal ? `${selectedTopic?.title || selectedTopic?.key} / ${activeModal.title}` : 'Natijalar'}
        </ModalHeader>
        <ModalBody className="psi-results-modal__body">
          {activeModal ? (
            <>
              <div className="psi-results-modal__toolbar">
                <div>
                  <h5 className="mb-1">{activeModal.title}</h5>
                  <p className="mb-0">Jadval pastida har bir savol bo`yicha ovozlar diagrammasi ko`rsatiladi.</p>
                </div>
                <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="psi-results-modal__page-size" />
              </div>

              <MedDataTable
                data={activeModal.rows}
                columns={activeModal.columns}
                pageSize={pageSize}
                paginationResetKey={`${activeModal.key}-${activeModal.rows.length}`}
                tableClasses="table table-hover fs--1 mb-0 med-dashboard-table"
                wrapperClasses="table-responsive border rounded"
              />

              <div className="psi-results-modal__charts">
                <div className="psi-results-modal__charts-head">
                  <h5 className="mb-1">Savollar bo`yicha ovozlar</h5>
                  <p className="mb-0">Har bir savolda nechta talaba qaysi variantni tanlagani shu yerda jamlangan.</p>
                </div>

                <div className="psi-results-chart-grid">
                  {(topicData.questions || []).map(question => (
                    <Card key={question.id || question.order} className="border-0 shadow-sm med-dashboard-card psi-results-chart-card">
                      <CardBody>
                        <div className="psi-results-chart-card__head">
                          <Badge pill className="psi-results-question-badge">
                            Savol {question.order}
                          </Badge>
                          <p className="mb-0">{question.question_text || `Savol ${question.order}`}</p>
                        </div>

                        <div className="psi-results-option-list">
                          {(question.options || []).map(option => (
                            <div key={`${question.order}-${option.key}`} className="psi-results-option-list__item">
                              <span className={`psi-results-option-dot psi-results-option-dot--${option.label.toLowerCase()}`}>
                                {option.label}
                              </span>
                              <div className="psi-results-option-list__copy">
                                <strong>{option.count} ta</strong>
                                <small>{formatPercent(option.percent)}</small>
                              </div>
                            </div>
                          ))}
                        </div>

                        <ReactEchartsCore
                          echarts={echarts}
                          option={buildChartOption(question)}
                          style={{ height: 240 }}
                          notMerge
                          lazyUpdate
                        />
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter className="psi-results-modal__footer">
          <Button
            color="light"
            className="psi-results-icon-button"
            onClick={handleExcelExport}
            title="Excel yuklab olish"
            disabled={!activeModal || exportRows.length === 0}
          >
            <FontAwesomeIcon icon="file-alt" />
          </Button>
          <Button
            color="light"
            className="psi-results-icon-button"
            onClick={handlePdfExport}
            title="PDF yuklab olish"
            disabled={!activeModal || exportRows.length === 0}
          >
            <FontAwesomeIcon icon="file-pdf" />
          </Button>
          <Button color="secondary" onClick={() => setActiveModalKey('')}>
            Yopish
          </Button>
        </ModalFooter>
      </Modal>

      {activeModal ? (
        <div className="psi-results-export-sheet" aria-hidden="true">
          <div ref={exportRef} className="psi-results-export-sheet__page">
            <div className="psi-results-export-sheet__header">
              <h2>{selectedTopic?.title || selectedTopic?.key}</h2>
              <div>{activeModal.title}</div>
              {activePeriodLabel ? <div>{activePeriodLabel}</div> : null}
            </div>
            <div className="psi-results-export-sheet__meta">
              <span>Natijalar: {topicData.summary?.response_count || 0}</span>
              <span>Savollar: {topicData.summary?.question_count || 0}</span>
              <span>Ortacha: {formatPercent(topicData.summary?.average_score_percent || 0)}</span>
            </div>
            <table className="psi-results-export-sheet__table">
              <thead>
                <tr>
                  {activeModal.exportColumns.map(column => (
                    <th key={column.label}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeModal.rows.map(row => (
                  <tr key={`${activeModal.key}-${row.id || row.order}`}>
                    {activeModal.exportColumns.map(column => (
                      <td key={`${row.id || row.order}-${column.label}`}>{column.value(row)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default CustomTestResults;
